import type { ReactElement } from 'react';
import {
  List,
  Datagrid,
  FunctionField,
  TextInput,
  SelectInput,
  RaRecord,
  TopToolbar,
  FilterButton,
  CreateButton,
  ExportButton,
} from 'react-admin';
import { useHasPermission } from '../admin/PermissionGate';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { INTAKE_QUEUE_CHOICES, INTAKE_QUEUE_FILTER } from '../admin/intake/intakeApi';
import { FEATURE_INTAKE_DEPARTMENTS } from '../admin/intake/intakeSides';
import { ARCHIVE_FILTER_CHOICES, ARCHIVE_FILTER_KEY, FEATURE_PRE_ORDER_ARCHIVE, isArchived } from './preOrderArchiveApi';
import { DateTimeField } from '../shared/DateFields';
import { Chip, Stack } from '@mui/material';
import { preOrderStatusColor, preOrderStatusLabel } from '../shared/status';
import { companyLabel } from '../shared/Panel';
import { wallClockDateTime } from '../shared/clientTime';
import {
  ClampedText,
  CompanyChip,
  compactDatagridSx,
} from '../shared/listCells';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FEATURE_PRE_ORDER_DOCUMENTS, ORIGIN_LABELS, readListDocuments } from './preOrderDocumentsApi';

/**
 * Lista prezleceń jak lista dokumentów (03.11.2026, `preOrderDocuments`,
 * S-03.11-01): numer, klient, relacja, terminy, stan z kompletem pól do
 * zapisu w TMS, liczba maili i plików, źródło. Liczby i komplet z API
 * (`documents` wiersza) — ekran ich nie liczy.
 */
export const ReadinessChip = ({ record }: { record: RaRecord }) => {
  const d = readListDocuments(record.documents);
  if (!d) return null;
  return d.ready ? (
    <Chip size="small" color="success" label="Gotowe do wysłania" data-testid="pre-order-send-ready" />
  ) : (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      label={`do TMS brakuje: ${d.missingCount}`}
      title="Prezlecenie jest zapisane; do zlecenia i zapisu w TMS brakuje pól — lista na karcie"
      data-testid="pre-order-send-missing"
    />
  );
};

const CountCell = ({ value, icon, testId }: { value: number; icon: ReactElement; testId: string }) => (
  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: value ? 'text.primary' : 'text.disabled' }} data-testid={testId}>
    {icon}
    <span>{value}</span>
  </Stack>
);

const documentsColumns = [
  <FunctionField
    key="number"
    label="Numer"
    sortBy="id"
    render={(r: RaRecord) => {
      const d = readListDocuments(r.documents);
      return (
        <Stack spacing={0}>
          <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{d?.orderNumber ?? `#${String(r.id)}`}</span>
          {r.customerOrderNumber ? <ClampedText text={r.customerOrderNumber} maxWidth={{ xs: 120, xl: 180 }} minWidth={80} /> : null}
        </Stack>
      );
    }}
  />,
  <FunctionField key="client" label="Klient" sortBy="clientName" render={(r: RaRecord) => <ClampedText text={r.clientName} maxWidth={{ xs: 160, xl: 240 }} minWidth={PRE_ORDER_TEXT_MIN_PX} />} />,
  <FunctionField key="relation" label="Relacja" sortBy="relation" render={(r: RaRecord) => <ClampedText text={r.relation ?? ([r.loadingCity, r.unloadingCity].some(Boolean) ? `${r.loadingCity ?? '?'} → ${r.unloadingCity ?? '?'}` : null)} maxWidth={{ xs: 170, xl: 260 }} minWidth={PRE_ORDER_TEXT_MIN_PX} />} />,
  <FunctionField key="loading" label="Załadunek" sortBy="loadingDate" render={(r: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{wallClockDateTime(r.loadingDate)}</span>} />,
  <FunctionField key="unloading" label="Rozładunek" sortBy="unloadingDate" render={(r: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{wallClockDateTime(r.unloadingDate)}</span>} />,
  <FunctionField
    key="state"
    label="Stan"
    render={(r: RaRecord) => (
      <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
        <PreOrderStatusChip record={r} />
        <ReadinessChip record={r} />
      </Stack>
    )}
  />,
  <FunctionField key="emails" label="Maile" render={(r: RaRecord) => <CountCell value={readListDocuments(r.documents)?.emailCount ?? 0} icon={<MailOutlineIcon sx={{ fontSize: 16 }} />} testId="pre-order-email-count" />} />,
  <FunctionField key="files" label="Pliki" render={(r: RaRecord) => <CountCell value={readListDocuments(r.documents)?.fileCount ?? 0} icon={<AttachFileIcon sx={{ fontSize: 16 }} />} testId="pre-order-file-count" />} />,
  <FunctionField
    key="origin"
    label="Źródło"
    render={(r: RaRecord) => {
      const d = readListDocuments(r.documents);
      return d ? <Chip size="small" variant="outlined" label={ORIGIN_LABELS[d.origin]} data-testid="pre-order-origin" /> : null;
    }}
  />,
];

export const PRE_ORDER_STATUSES = [
  'NOWE',
  'W_WERYFIKACJI',
  'UZUPELNIANE',
  'ZATWIERDZONE',
  'ODRZUCONE',
];
const statusChoices = PRE_ORDER_STATUSES.map((id) => ({ id, name: preOrderStatusLabel(id) }));

export const PreOrderStatusChip = ({ record }: { record: RaRecord }) => {
  const s = (record.status as string) ?? 'NOWE';
  const missing = (record.missingFields as string[] | undefined) ?? [];
  return (
    // Poniżej 1366 px znaczniki schodzą do drugiej linii, gdy tabeli brakuje
    // miejsca (30.10.2026) — w jednym wierszu trzymały kolumnę na ok. 180 px.
    // Od 1366 px w jednym wierszu, jak dotąd.
    <Stack direction="row" spacing={0.5} useFlexGap sx={STATUS_CHIPS_SX} data-testid="pre-order-status">
      {/* Zarchiwizowane (25.10.2026, `preOrderArchive`) — widać je tylko z filtrem „Pokaż zarchiwizowane”. */}
      {isArchived(record) ? (
        <Chip size="small" color="default" variant="outlined" label="zarchiwizowane" data-testid="pre-order-archived-chip" />
      ) : null}
      <Chip size="small" color={preOrderStatusColor(s)} label={preOrderStatusLabel(s)} />
      <Chip
        size="small"
        variant="outlined"
        color={missing.length ? 'warning' : 'success'}
        label={missing.length ? `braki: ${missing.length}` : 'komplet'}
      />
    </Stack>
  );
};

/**
 * Najmniejsza szerokość klienta, relacji i towaru (30.10.2026, decyzja Bartka:
 * lista prezleceń bez przewijania w bok także w wyglądzie jak dotąd). Przy
 * wolnym miejscu kolumny mają szerokość jak dotąd (`maxWidth`), przy wąskim
 * oknie ściskają się do tej wartości — tekst kończy się wielokropkiem,
 * pełny w podpowiedzi.
 */
export const PRE_ORDER_TEXT_MIN_PX = 96;

/** Znaczniki statusu: zawijane tylko poniżej 1366 px (`PreOrderStatusChip`). */
export const STATUS_CHIPS_SX = {
  alignItems: 'center',
  flexWrap: 'nowrap',
  '@media (max-width:1365.95px)': { flexWrap: 'wrap' },
} as const;

/** Znacznik obiegu z długim opisem („Błąd zapisu — ponawiane automatycznie”) łamie się w dwie linie. */
const WRAPPING_CHIP_SX = { height: 'auto', minHeight: 24, '& .MuiChip-label': { whiteSpace: 'normal' } } as const;

const filters = [
  <TextInput source="q" label="Klient, miasto, towar" alwaysOn />,
  <SelectInput source="status" label="Status" choices={statusChoices} />,
];

/**
 * Kolejka działu przyjęcia zleceń (makieta back office v2, fala B, B1,
 * 21.10.2026; przełącznik `intakeDepartments`) — filtr i kolumna. Klucz
 * `intakeQueue` to kontrakt z API (react-admin go pamięta); kafelki pulpitu
 * przyjęcia zleceń prowadzą tu z tym samym filtrem.
 */
const intakeFilters = [
  ...filters,
  <SelectInput
    key={INTAKE_QUEUE_FILTER}
    source={INTAKE_QUEUE_FILTER}
    label="Kolejka działu"
    choices={INTAKE_QUEUE_CHOICES.map(({ id, name }) => ({ id, name }))}
    alwaysOn
  />,
];

/** Etap obiegu i kolejka wiersza — z API (`intake`), ta sama reguła co filtr. */
export const IntakeCell = ({ record }: { record: RaRecord }) => {
  const intake = record.intake as
    | { queueLabel: string | null; stateLabel: string | null; returnCount: number | null }
    | undefined;
  if (!intake) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }} data-testid="pre-order-intake">
      {intake.stateLabel ? <Chip size="small" variant="outlined" label={intake.stateLabel} sx={WRAPPING_CHIP_SX} /> : null}
      {intake.queueLabel ? <Chip size="small" color="info" variant="outlined" label={intake.queueLabel} sx={WRAPPING_CHIP_SX} /> : null}
      {intake.returnCount ? <Chip size="small" color="warning" variant="outlined" label={`zwroty: ${intake.returnCount}`} /> : null}
    </Stack>
  );
};

/**
 * Pasek nad listą jak domyślny react-admina, ale „Utwórz” tylko z przyjęciem
 * zleceń (01.10.2026): prezlecenie wpisuje CS albo Administracja, FTL i Trade
 * mają podgląd (Administracja §15). Bez uprawnienia przycisku nie ma wcale.
 */
export const PreOrderListActions = () => {
  const canIntake = useHasPermission('ORDER_INTAKE');
  return (
    <TopToolbar>
      <FilterButton />
      {canIntake && <CreateButton />}
      <ExportButton />
    </TopToolbar>
  );
};

/**
 * „Pokaż zarchiwizowane” (25.10.2026, decyzja użytkownika 24.10.2026, A6;
 * `preOrderArchive`): bez filtra lista nie ma zarchiwizowanych. Klucz
 * `archived` to kontrakt z API.
 */
const archiveFilter = (
  <SelectInput
    key={ARCHIVE_FILTER_KEY}
    source={ARCHIVE_FILTER_KEY}
    label="Zarchiwizowane"
    choices={ARCHIVE_FILTER_CHOICES}
    emptyText="Bez zarchiwizowanych"
    data-testid="pre-order-archived-filter"
  />
);

// Prezlecenia — bogaty rekord roboczy wyciągnięty z maila (W3). Jeden mail może
// dać kilka prezleceń (lista ładunków / długi wątek) — patrz kolumna „Zlecenia".
export const PreOrderList = () => {
  const intakeOn = useFeature(FEATURE_INTAKE_DEPARTMENTS);
  const archiveOn = useFeature(FEATURE_PRE_ORDER_ARCHIVE);
  const documentsOn = useFeature(FEATURE_PRE_ORDER_DOCUMENTS);
  const listFilters = intakeOn ? intakeFilters : filters;
  return (
  <List
    filters={archiveOn ? [...listFilters, archiveFilter] : listFilters}
    actions={<PreOrderListActions />}
    sort={{ field: 'createdAt', order: 'DESC' }}
    perPage={25}
  >
    {/* Długie teksty skracane z podpowiedzią, spółka jako znacznik z krajem,
        czas bez sekund — wiersz w jednej linii od 1366 px (28.09.2026). */}
    <Datagrid rowClick="show" bulkActionButtons={false} sx={compactDatagridSx} data-testid={documentsOn ? 'pre-order-list-documents' : undefined}>
      {documentsOn ? documentsColumns : null}
      {documentsOn ? null : <FunctionField label="Klient" render={(r: RaRecord) => <ClampedText text={r.clientName} maxWidth={{ xs: 160, xl: 260 }} minWidth={PRE_ORDER_TEXT_MIN_PX} />} />}
      {documentsOn ? null : <FunctionField label="Relacja" render={(r: RaRecord) => <ClampedText text={r.relation} maxWidth={{ xs: 180, xl: 300 }} minWidth={PRE_ORDER_TEXT_MIN_PX} />} />}
      {documentsOn ? null : <FunctionField label="Towar" render={(r: RaRecord) => <ClampedText text={r.cargo} maxWidth={{ xs: 160, xl: 260 }} minWidth={PRE_ORDER_TEXT_MIN_PX} />} />}
      {/* Czas polski z zapisu, nie strefa przeglądarki (`shared/clientTime.ts`). */}
      {documentsOn ? null : (
        <FunctionField
          label="Załadunek"
          sortBy="loadingDate"
          render={(r: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{wallClockDateTime(r.loadingDate)}</span>}
        />
      )}
      {documentsOn ? null : <FunctionField label="Status" render={(r: RaRecord) => <PreOrderStatusChip record={r} />} />}
      {intakeOn ? <FunctionField label="Obieg" render={(r: RaRecord) => <IntakeCell record={r} />} /> : null}
      {documentsOn ? null : <FunctionField label="Zlecenia" render={(r: RaRecord) => (r._count?.orders ?? 0)} />}
      <FunctionField
        label="Spółka"
        render={(r: RaRecord) => (
          <CompanyChip
            name={r.company?.name ?? companyLabel(r.company?.viaCompanyId ?? undefined)}
            country={r.company?.country}
          />
        )}
      />
      <DateTimeField source="createdAt" label="Utworzono" />
    </Datagrid>
  </List>
  );
};
